import {
  advancedGameShape,
  advancedGameDefaults,
  propertyGroups,
  propertyDescriptions,
  propertyReadOnlyReason,
  GameSettingsSchema,
  type GameSettings,
} from '@mcpanel/core';

export function ServerPropertiesFields({
  value,
  onChange,
  disabled,
}: {
  value: GameSettings;
  onChange: (value: GameSettings) => void;
  disabled: boolean;
}) {
  const parsed = GameSettingsSchema.safeParse(value);
  const readOnly = { 'generate-structures': 'true', ...value.readOnlyProperties };
  return (
    <div className="stack">
      <h3>サーバープロパティの詳細</h3>
      <p>
        版に応じてプロパティ・ゲームルールを書き分けます。ゲームルールは起動中に現在値を確認でき、停止中の変更は次回起動後に反映します。
      </p>
      {Object.entries(propertyGroups).map(([group, keys]) => (
        <fieldset key={group} disabled={disabled} className="stack">
          <legend>{group}</legend>
          {keys.map((key) => {
            const defaultValue = advancedGameDefaults[key];
            const schema = advancedGameShape[key];
            const issue = parsed.success ? undefined : parsed.error.issues.find((entry) => entry.path[0] === key);
            // zod の英語のメッセージではなく、入れられる範囲を日本語で出す
            const error =
              issue &&
              ('minValue' in schema && schema.minValue !== null && 'maxValue' in schema && schema.maxValue !== null
                ? `${schema.minValue} 以上 ${schema.maxValue} 以下の整数を入れてください`
                : issue.message);
            return (
              <div className="field" key={key} data-search-id={`property-${key}`}>
                <label htmlFor={`property-${key}`}>{key}</label>
                <p className="small muted">
                  {propertyDescriptions[key]} 既定値:{' '}
                  {value.routing?.rules[key as keyof typeof value.routing.rules] ? 'true' : String(defaultValue)}
                </p>
                {typeof defaultValue === 'boolean' ? (
                  <input
                    id={`property-${key}`}
                    type="checkbox"
                    checked={Boolean(value[key])}
                    disabled={value.routing?.unsupported.includes(key as 'spawn-animals' | 'spawn-npcs')}
                    ref={(input) => {
                      if (input) input.indeterminate = value[key] === null;
                    }}
                    onChange={(e) => onChange({ ...value, [key]: e.target.checked })}
                  />
                ) : (
                  <input
                    className="input"
                    id={`property-${key}`}
                    type="number"
                    step={1}
                    min={'minValue' in schema ? (schema.minValue ?? undefined) : undefined}
                    max={'maxValue' in schema ? (schema.maxValue ?? undefined) : undefined}
                    value={Number(value[key])}
                    aria-invalid={Boolean(error)}
                    onChange={(e) =>
                      onChange({ ...value, [key]: e.target.value === '' ? NaN : Number(e.target.value) })
                    }
                  />
                )}
                {value.settingMessages?.[key] && <p className="small muted">{value.settingMessages[key]}</p>}
                {error && <p role="alert">{error}</p>}
              </div>
            );
          })}
        </fieldset>
      ))}
      <h3>生成・パネル管理項目（表示のみ）</h3>
      <p>ワールドの生成は、このタブの「ワールドの生成」で設定してください。</p>
      {[
        'server-port',
        'server-ip',
        'online-mode',
        'enable-rcon',
        'rcon.port',
        'rcon.password',
        'level-name',
        'enable-query',
        'query.port',
        'generate-structures',
        'level-seed',
        'level-type',
        'generator-settings',
      ].map((key) => (
        <div key={key} data-search-id={`property-${key}`}>
          <strong>{key}</strong>: {readOnly[key as keyof typeof readOnly] ?? '（未設定）'}
          <p className="small muted">{propertyReadOnlyReason(key)}</p>
        </div>
      ))}
      <h3>その他の項目（表示のみ）</h3>
      {Object.entries(readOnly)
        .filter(
          ([key]) =>
            ![
              'server-port',
              'server-ip',
              'online-mode',
              'enable-rcon',
              'rcon.port',
              'rcon.password',
              'level-name',
              'enable-query',
              'query.port',
              'generate-structures',
              'level-seed',
              'level-type',
              'generator-settings',
            ].includes(key),
        )
        .map(([key, text]) => (
          <div key={key} data-search-id={`property-${key}`}>
            <strong>{key}</strong>: {text}
            <p className="small muted">{propertyReadOnlyReason(key)}</p>
          </div>
        ))}
    </div>
  );
}
